import { lazy, type LazyExoticComponent } from "react";

/**
 * Creates a cached lazy-loader from a Vite `import.meta.glob` module map.
 *
 * Returns a lookup function: given a module path, it returns the cached
 * `React.lazy` component — or `null` if the path doesn't exist in the map.
 *
 * @example
 * ```ts
 * const modules = import.meta.glob<{ default: React.ComponentType }>(
 *   "../../assets/icons/*.svg",
 *   { query: "?react" },
 * );
 * const getLazy = createLazyLoader(modules);
 * const LazyIcon = getLazy("../../assets/icons/check.svg");
 * ```
 */
export function createLazyLoader<T extends React.ComponentType<any>>(
  modules: Record<string, () => Promise<{ default: T }>>,
): (path: string) => LazyExoticComponent<T> | null {
  const cache = new Map<string, LazyExoticComponent<T>>();

  return (path: string) => {
    const cached = cache.get(path);
    if (cached) return cached;

    const loader = modules[path];
    if (!loader) return null;

    const component = lazy(loader);
    cache.set(path, component);
    return component;
  };
}
